import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { reviewCycleKeys } from "@/lib/query";
import type { ReviewCycleCreateInput, ReviewCycleUpdateInput } from "@/schemas/review-cycles";

export interface ReviewCycle {
  id: string;
  name: string;
  description: string | null;
  statusKey: string;
  cycleTypeKey: string | null;
  excludeStartedAfter: string | null;
  periodStart: string;
  periodEnd: string;
  opensOn: string;
  dueOn: string;
  minimumOverlapDays: number | null;
  /** Endorsements each reviewer may give per review in this round; 0 turns them off. */
  maxEndorsements: number;
  /** Days before the due date managers are reminded to hold the conversation; null is off. */
  managerConversationLeadDays: number | null;
  defaultTemplateId: string | null;
  generatedAt: string | null;
  closedAt: string | null;
  reviewCount: number;
  completeReviewCount: number;
  /** EPIC-057 WI-205. */
  excludedReviewerCount: number;
}

export type ExclusionReason =
  | "no_primary_role"
  | "no_manager"
  | "no_matching_form"
  | "no_role_competencies"
  | "started_after_cutoff"
  | "not_named"
  | "employment_type"
  | "excluded_by_admin";

export interface RosterPerson {
  personId: string;
  personName: string;
  roleId: string | null;
  roleName: string | null;
  roleLevelId: string | null;
  roleLevelName: string | null;
  managerPersonId: string | null;
  managerName: string | null;
  employmentTypeKey: string | null;
  employmentTypeName: string | null;
  templateId: string | null;
  templateName: string | null;
  included: boolean;
  reason: ExclusionReason | null;
  canIncludeByDecision: boolean;
  isOverridden: boolean;
  isNew: boolean;
  reviewId: string | null;
  templateIsFallback: boolean;
}

export interface FormInUse {
  templateId: string;
  templateName: string;
  subjectCount: number;
  taskCount: number;
  /** Matched by role scope rather than named by the round — the old model. */
  isFallback: boolean;
  asks: { relationshipKey: string; name: string; generatesToday: boolean }[];
}

/** Somebody who still owes work in an open round. */
export interface Straggler {
  personId: string;
  personName: string;
  outstanding: number;
  reachable: boolean;
  lastNotifiedKind: "invitation" | "reminder" | "overdue" | "completed" | null;
  /** Whose reviews they still owe. */
  owes: { reviewerId: string; subjectName: string; relationshipName: string }[];
}

/** A review that has reached complete/closed, and who still owes a sign-off — EPIC-065 WI-240. */
export interface SignOffStatus {
  reviewId: string;
  subjectPersonId: string;
  subjectName: string;
  awaiting: "manager" | "employee" | "both" | "none";
  daysOutstanding: number;
}

/** One notification the round sent, or held back — EPIC-081 WI-306. */
export interface NotificationLogEntry {
  id: string;
  at: string;
  recipientEmail: string;
  recipientName: string | null;
  trigger: string;
  subject: string;
  outcome: "queued" | "sent" | "delivered" | "problem" | "held_back";
  detail: string | null;
  isTest: boolean;
}

/** How many reviews one person was asked to do in a round — EPIC-073 WI-277. */
export interface ReviewerWorkloadRow {
  personId: string;
  personName: string;
  total: number;
  completed: number;
  byRelationship: { relationshipKey: string; relationshipName: string; count: number }[];
}

/** One excluded reviewer, anywhere in a round — EPIC-057 WI-204. */
export interface ExcludedReviewer {
  reviewId: string;
  reviewerId: string;
  reviewerName: string;
  relationshipName: string;
  subjectName: string;
  excludedAt: string;
}

export interface GenerationPreview {
  roster: RosterPerson[];
  includedCount: number;
  toGenerate: number;
  alreadyGenerated: number;
  byEmploymentType: { key: string; name: string; count: number }[];
  formsInUse: FormInUse[];
  peopleWithoutForm: number;
}

async function failure(res: Response, fallback: string): Promise<Error> {
  const json = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return new Error(typeof json?.error === "string" && json.error ? json.error : fallback);
}

export function useReviewCycles() {
  return useQuery({
    queryKey: reviewCycleKeys.all,
    queryFn: async (): Promise<ReviewCycle[]> => {
      const res = await fetch("/api/review-cycles");
      if (!res.ok) throw await failure(res, "Failed to load review rounds");
      return (await res.json()).data;
    },
  });
}

export function useReviewCycle(id: string | null) {
  return useQuery({
    queryKey: reviewCycleKeys.detail(id ?? ""),
    queryFn: async (): Promise<ReviewCycle> => {
      const res = await fetch(`/api/review-cycles/${id}`);
      if (!res.ok) throw await failure(res, "Failed to load this round");
      return (await res.json()).data;
    },
    enabled: Boolean(id),
  });
}

export function useGenerationPreview(id: string | null) {
  return useQuery({
    queryKey: reviewCycleKeys.preview(id ?? ""),
    queryFn: async (): Promise<GenerationPreview> => {
      const res = await fetch(`/api/review-cycles/${id}/preview`);
      if (!res.ok) throw await failure(res, "Failed to work out who this round covers");
      return (await res.json()).data;
    },
    enabled: Boolean(id),
  });
}

/** Only asked for while a round is open — a closed round has nobody to chase. */
export function useStragglers(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: reviewCycleKeys.stragglers(id ?? ""),
    queryFn: async (): Promise<Straggler[]> => {
      const res = await fetch(`/api/review-cycles/${id}/stragglers`);
      if (!res.ok) throw await failure(res, "Failed to work out who is still outstanding");
      return (await res.json()).data;
    },
    enabled: Boolean(id) && enabled,
  });
}

/** How many reviews one person was asked to do in a round, and how much is done — EPIC-073 WI-277. */
export interface ReviewerProgressRow {
  personId: string;
  personName: string;
  total: number;
  completed: number;
  byRelationship: { relationshipKey: string; relationshipName: string; count: number }[];
}

/**
 * How loaded each reviewer was in this round, and how much they've finished —
 * EPIC-073 WI-277. A different question from `useReviewerWorkload`'s flat
 * assignment count (EPIC-059 WI-316): this breaks the total down by
 * relationship and tracks completion, for progress tracking rather than
 * roster rebalancing. Unlike stragglers, worth asking for however the round
 * stands: how unevenly a CLOSED round was loaded is still the fact worth
 * seeing before the next one is generated.
 */
export function useReviewerProgress(id: string | null) {
  return useQuery({
    queryKey: reviewCycleKeys.workload(id ?? ""),
    queryFn: async (): Promise<ReviewerProgressRow[]> => {
      const res = await fetch(`/api/review-cycles/${id}/workload`);
      if (!res.ok) throw await failure(res, "Failed to work out reviewer workload");
      return (await res.json()).data;
    },
    enabled: Boolean(id),
  });
}

/**
 * Who is still waiting on a sign-off — EPIC-065 WI-240. Asked for once the
 * round has at least one review complete/closed; an empty list from a round
 * still fully open would just be a confusing blank section.
 */
export function useReviewSignOffStatus(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: reviewCycleKeys.signOffStatus(id ?? ""),
    queryFn: async (): Promise<SignOffStatus[]> => {
      const res = await fetch(`/api/review-cycles/${id}/sign-off-status`);
      if (!res.ok)
        throw await failure(res, "Failed to work out who is still waiting on a sign-off");
      return (await res.json()).data;
    },
    enabled: Boolean(id) && enabled,
  });
}

/** What a round has emailed, and what became of it — EPIC-081 WI-306. */
export function useReviewNotificationLog(id: string, search: string, enabled: boolean) {
  return useQuery({
    queryKey: reviewCycleKeys.notificationLog(id, search),
    queryFn: async (): Promise<NotificationLogEntry[]> => {
      const res = await fetch(
        `/api/review-cycles/${id}/notification-log?search=${encodeURIComponent(search)}`,
      );
      if (!res.ok) throw await failure(res, "Failed to load the notification log");
      return (await res.json()).data;
    },
    enabled,
  });
}

/**
 * Every excluded reviewer across a round — EPIC-057 WI-204. Always asked
 * for (unlike the other round sections): an exclusion can happen the moment
 * a round opens and should never go unnoticed because nobody asked yet.
 */
export function useExcludedReviewers(id: string | null) {
  return useQuery({
    queryKey: reviewCycleKeys.excludedReviewers(id ?? ""),
    queryFn: async (): Promise<ExcludedReviewer[]> => {
      const res = await fetch(`/api/review-cycles/${id}/excluded-reviewers`);
      if (!res.ok) throw await failure(res, "Failed to load excluded reviewers");
      return (await res.json()).data;
    },
    enabled: Boolean(id),
  });
}

/** One reviewer's total across the round, heaviest first — EPIC-059 WI-316. */
export interface ReviewerWorkloadRow {
  reviewerPersonId: string;
  reviewerName: string;
  assignedCount: number;
}

/**
 * Reviewer totals for the round — WI-316. Kori: leaders and people rolled
 * across several projects get handed an unrealistic number, and she strips
 * assignments out by hand once she sees it. Live, not asked for by default
 * the way `useExcludedReviewers` is — this reads as a count first, so it's
 * fetched eagerly but only rendered once the round has reviewers at all.
 */
export function useReviewerWorkload(id: string | null) {
  return useQuery({
    queryKey: reviewCycleKeys.reviewerWorkload(id ?? ""),
    queryFn: async (): Promise<ReviewerWorkloadRow[]> => {
      const res = await fetch(`/api/review-cycles/${id}/reviewer-workload`);
      if (!res.ok) throw await failure(res, "Failed to load reviewer workload");
      return (await res.json()).data;
    },
    enabled: Boolean(id),
  });
}

export interface ReviewerAssignment {
  reviewId: string;
  reviewerId: string;
  subjectPersonId: string;
  subjectName: string;
  relationshipName: string;
}

/** Who one reviewer in the round is assigned to — the expansion WI-316 asks for. */
export function useReviewerAssignments(cycleId: string | null, reviewerPersonId: string | null) {
  return useQuery({
    queryKey: [...reviewCycleKeys.reviewerWorkload(cycleId ?? ""), reviewerPersonId],
    queryFn: async (): Promise<ReviewerAssignment[]> => {
      const res = await fetch(
        `/api/review-cycles/${cycleId}/reviewer-workload/${reviewerPersonId}`,
      );
      if (!res.ok) throw await failure(res, "Failed to load their assignments");
      return (await res.json()).data;
    },
    enabled: Boolean(cycleId) && Boolean(reviewerPersonId),
  });
}

/**
 * Remove one assignment from the workload view — WI-316 AC: "An assignment
 * can be removed from this view, and the totals update." Reuses the same
 * exclusion `setReviewerExcluded` already applies (EPIC-057): the row and
 * any answer already given are kept, only `excluded_at` changes.
 */
export function useRemoveWorkloadAssignment(cycleId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reviewId, reviewerId }: { reviewId: string; reviewerId: string }) => {
      const res = await fetch(`/api/reviews/${reviewId}/reviewers/${reviewerId}/exclusion`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ excluded: true }),
      });
      if (!res.ok) throw await failure(res, "Couldn't remove that assignment");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewCycleKeys.reviewerWorkload(cycleId ?? "") });
      toast.success("Assignment removed");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useCreateReviewCycle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReviewCycleCreateInput): Promise<ReviewCycle> => {
      const res = await fetch("/api/review-cycles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await failure(res, "Couldn't create the round");
      return (await res.json()).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewCycleKeys.all });
      toast.success("Review round created");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useUpdateReviewCycle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReviewCycleUpdateInput) => {
      const res = await fetch(`/api/review-cycles/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await failure(res, "Couldn't save the round");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewCycleKeys.all });
      toast.success("Saved");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** Generate for whoever is currently in the round. */
export function useGenerateCycle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/review-cycles/${id}/generate`, { method: "POST" });
      if (!res.ok) throw await failure(res, "Couldn't generate this round");
      return (await res.json()).data as {
        reviewsCreated: number;
        reviewersCreated: number;
        responsesSeeded: number;
        alreadyPresent: number;
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: reviewCycleKeys.all });
      toast.success(
        data.reviewsCreated === 0
          ? "Nothing new to generate"
          : `${data.reviewsCreated} review${data.reviewsCreated === 1 ? "" : "s"} generated`,
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * Put one person in or out of a round by hand, or clear the override with
 * `decision: null` and let the rules decide again.
 */
export function useSetCycleParticipant(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      personId,
      decision,
      note,
    }: {
      personId: string;
      decision: "include" | "exclude" | null;
      note?: string | null;
    }) => {
      const res = await fetch(`/api/review-cycles/${cycleId}/participants/${personId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, note: note ?? null }),
      });
      if (!res.ok) throw await failure(res, "Couldn't change who is in this round");
      return res.json();
    },
    onSuccess: (_data, { decision }) => {
      queryClient.invalidateQueries({ queryKey: reviewCycleKeys.all });
      toast.success(
        decision === "exclude"
          ? "Taken out of this round"
          : decision === "include"
            ? "Added to this round"
            : "Back to the usual rules",
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useSetReviewCycleStatus(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (status: "open" | "closed") => {
      const res = await fetch(`/api/review-cycles/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw await failure(res, "Couldn't change the round's status");
      return res.json();
    },
    onSuccess: (_data, status) => {
      queryClient.invalidateQueries({ queryKey: reviewCycleKeys.all });
      toast.success(status === "open" ? "Round opened" : "Round closed");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** An answer to a question a form marked People-Ops-only — WI-281. */
export interface ConfidentialAnswer {
  reviewId: string;
  subjectPersonId: string;
  subjectName: string;
  roleName: string;
  roleLevelName: string | null;
  reviewerName: string;
  relationshipName: string;
  question: string;
  rating: string | null;
  comment: string | null;
}

/**
 * The confidential answers for a round. Admin-only server-side; `enabled`
 * keeps a non-admin from firing a request that would only 403.
 */
export function useConfidentialAnswers(cycleId: string, enabled: boolean) {
  return useQuery({
    queryKey: reviewCycleKeys.confidentialAnswers(cycleId),
    queryFn: async (): Promise<ConfidentialAnswer[]> => {
      const res = await fetch(`/api/review-cycles/${cycleId}/confidential-answers`);
      if (!res.ok) throw new Error("Couldn't load the People Ops answers");
      return (await res.json()).data;
    },
    enabled,
  });
}
